import { useEffect, useState, useCallback, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppHeader from '../../components/AppHeader';
import { useAuth } from '../../contexts/MemberAuthContext';
import { tripsApi, inventoryApi, handoverApi, vehiclesApi, containersApi } from '../../api';
import { TRIP_STATUS, TRIP_STATUS_BADGE, HANDOVER_MODES, NEXT_STATUSES, PLACEMENT_LABELS, fuelLabel } from '../../utils/trips';

const METHOD_LABEL = { qr: 'QR', manual_ops: 'OpS ruční' };
const emptyCargo = { unitId: '', placement: 'vehicle', mode: 'inventory', source_inventory_id: '', custom_name: '', unit: 'ks', quantity: 1, note: '' };
const emptyUnit = { vehicle_id: '', trailer_id: '', driver_id: '' };

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { member } = useAuth();
  const canManage = ['superadmin', 'ops', 'chapter_rep'].includes(member.role);
  const isOps = ['superadmin', 'ops'].includes(member.role);

  const [trip, setTrip] = useState(null);
  const [units, setUnits] = useState([]);
  const [cargo, setCargo] = useState([]);
  const [handoverEvents, setHandoverEvents] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [boxContents, setBoxContents] = useState({}); // cargoId -> [items]
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [cargoForm, setCargoForm] = useState(null);   // null nebo emptyCargo s unitId
  const [unitForm, setUnitForm] = useState(null);      // null nebo emptyUnit
  const [busy, setBusy] = useState(() => new Set());   // skladové položky s probíhajícím requestem
  const [fuelEnd, setFuelEnd] = useState('');
  const [error, setError] = useState('');

  const loadCargo = useCallback(() => tripsApi.cargo(id).then((r) => setCargo(r.data)).catch(() => {}), [id]);
  const loadUnits = useCallback(() => tripsApi.units(id).then((r) => setUnits(r.data)).catch(() => {}), [id]);
  const loadHandoverEvents = useCallback(() =>
    handoverApi.tripHandoverEvents(id).then((r) => setHandoverEvents(r.data)).catch(() => {}), [id]);

  const loadTrip = useCallback(() => {
    tripsApi.get(id).then((r) => {
      setTrip(r.data);
      setFuelEnd(r.data.fuel_end != null ? String(r.data.fuel_end) : '');
      const chap = r.data.chapter_id;
      inventoryApi.list(chap).then((inv) => setInventory(inv.data)).catch(() => setInventory([]));
      containersApi.list(isOps ? chap : undefined).then((b) => setBoxes(b.data)).catch(() => setBoxes([]));
      if (canManage) {
        tripsApi.drivers(isOps ? chap : undefined).then((d) => setDrivers(d.data)).catch(() => setDrivers([]));
        vehiclesApi.list(isOps ? chap : undefined).then((v) => setVehicles(v.data)).catch(() => setVehicles([]));
      }
    }).catch(() => navigate('/cesty'));
  }, [id, navigate, canManage, isOps]);

  useEffect(() => { loadTrip(); loadUnits(); loadCargo(); loadHandoverEvents(); }, [loadTrip, loadUnits, loadCargo, loadHandoverEvents]);

  const refresh = () => { loadTrip(); loadUnits(); loadCargo(); };

  const changeStatus = async (status) => {
    setError('');
    try {
      const fe = fuelEnd === '' ? null : Number(fuelEnd);
      const res = await tripsApi.setStatus(id, status, (status === 'arrived' || status === 'completed') ? fe : null);
      setTrip(res.data);
    } catch (err) { setError(err.response?.data?.error || 'Změna stavu selhala.'); }
  };

  // ── Jednotky konvoje ──────────────────────────────────────────────────────
  const addUnit = async (e) => {
    e.preventDefault();
    setError('');
    if (!unitForm.vehicle_id) { setError('Vyberte vozidlo.'); return; }
    try {
      await tripsApi.addUnit(id, {
        vehicle_id: unitForm.vehicle_id,
        trailer_id: unitForm.trailer_id || null,
        driver_id: unitForm.driver_id || null,
      });
      setUnitForm(null); refresh();
    } catch (err) { setError(err.response?.data?.error || 'Přidání vozidla selhalo.'); }
  };

  const removeUnit = async (tvId) => {
    if (!confirm('Odebrat toto vozidlo z konvoje (včetně jeho nákladu)?')) return;
    setError('');
    try { await tripsApi.deleteUnit(id, tvId); refresh(); }
    catch (err) { setError(err.response?.data?.error || 'Odebrání selhalo.'); }
  };

  // ── Náklad ────────────────────────────────────────────────────────────────
  const openCargoForm = (unit) => {
    setError('');
    setCargoForm({ ...emptyCargo, unitId: unit.id, placement: 'vehicle' });
  };

  const addCargo = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const base = { trip_vehicle_id: cargoForm.unitId, placement: cargoForm.placement,
        quantity: Number(cargoForm.quantity) || 0, note: cargoForm.note || null };
      const payload = cargoForm.mode === 'inventory'
        ? { ...base, source_inventory_id: cargoForm.source_inventory_id }
        : { ...base, custom_name: cargoForm.custom_name, unit: cargoForm.unit };
      if (cargoForm.mode === 'inventory' && !payload.source_inventory_id) { setError('Vyberte položku ze skladu.'); return; }
      if (cargoForm.mode === 'custom' && !payload.custom_name.trim()) { setError('Zadejte název položky.'); return; }
      await tripsApi.addCargo(id, payload);
      setCargoForm(null); refresh();
    } catch (err) { setError(err.response?.data?.error || 'Přidání nákladu selhalo.'); }
  };

  const removeCargo = async (cargoId) => {
    if (!confirm('Odebrat položku z nákladu?')) return;
    await tripsApi.deleteCargo(id, cargoId);
    refresh();
  };

  // Naložená položka (ze skladu) pro danou jednotku + umístění.
  const loadedCargo = (unitId, placement, invId) =>
    cargo.find((c) => c.trip_vehicle_id === unitId && c.placement === placement && c.source_inventory_id === invId);

  const setItemBusy = (invId, on) => setBusy((b) => { const n = new Set(b); on ? n.add(invId) : n.delete(invId); return n; });

  // „+" naloží 1 ks položky ze skladu na jednotku (vytvoří/inkrementuje), odečte sklad.
  const incItem = async (unit, placement, item) => {
    if (busy.has(item.id) || item.quantity <= 0) return;
    setError(''); setItemBusy(item.id, true);
    try {
      const existing = loadedCargo(unit.id, placement, item.id);
      if (existing) {
        const r = await tripsApi.updateCargo(id, existing.id, { quantity: existing.quantity + 1 });
        setCargo((cs) => cs.map((c) => c.id === existing.id ? { ...c, quantity: r.data.quantity } : c));
      } else {
        const r = await tripsApi.addCargo(id, { trip_vehicle_id: unit.id, placement, source_inventory_id: item.id, quantity: 1 });
        setCargo((cs) => [...cs, r.data]);
      }
      setInventory((inv) => inv.map((it) => it.id === item.id ? { ...it, quantity: it.quantity - 1 } : it));
    } catch (err) { setError(err.response?.data?.error || 'Nakládka selhala.'); refresh(); }
    finally { setItemBusy(item.id, false); }
  };

  // „−" odebere 1 ks (smaže při dosažení 0), vrátí na sklad.
  const decItem = async (unit, placement, item) => {
    const existing = loadedCargo(unit.id, placement, item.id);
    if (!existing || busy.has(item.id)) return;
    setError(''); setItemBusy(item.id, true);
    try {
      if (existing.quantity > 1) {
        const r = await tripsApi.updateCargo(id, existing.id, { quantity: existing.quantity - 1 });
        setCargo((cs) => cs.map((c) => c.id === existing.id ? { ...c, quantity: r.data.quantity } : c));
      } else {
        await tripsApi.deleteCargo(id, existing.id);
        setCargo((cs) => cs.filter((c) => c.id !== existing.id));
      }
      setInventory((inv) => inv.map((it) => it.id === item.id ? { ...it, quantity: it.quantity + 1 } : it));
    } catch (err) { setError(err.response?.data?.error || 'Odebrání selhalo.'); refresh(); }
    finally { setItemBusy(item.id, false); }
  };

  // Naloží celý eurobox na jednotku.
  const loadBox = async (unit, placement, box) => {
    setError('');
    try {
      await tripsApi.addCargo(id, { trip_vehicle_id: unit.id, placement, container_id: box.id });
      setCargoForm(null); refresh();
    } catch (err) { setError(err.response?.data?.error || 'Naložení boxu selhalo.'); }
  };

  // Rozbalí/sbalí obsah naloženého boxu (řádek nákladu).
  const toggleBoxContents = (cargoItem) => {
    setBoxContents((prev) => {
      if (prev[cargoItem.id]) { const n = { ...prev }; delete n[cargoItem.id]; return n; }
      containersApi.items(cargoItem.container_id).then((r) => setBoxContents((p) => ({ ...p, [cargoItem.id]: r.data }))).catch(() => {});
      return prev;
    });
  };

  const removeTrip = async () => {
    if (!confirm('Opravdu smazat celou cestu včetně nákladu?')) return;
    await tripsApi.delete(id);
    navigate('/cesty');
  };

  if (!trip) return <div className="container" style={{ paddingTop: 48 }}><div className="spinner" /></div>;

  const cargoForUnit = (tvId) => cargo.filter((c) => c.trip_vehicle_id === tvId);
  const usedVehicleIds = new Set(units.flatMap((u) => [u.vehicle_id, u.trailer_id].filter(Boolean)));
  const freeVehicles = vehicles.filter((v) => v.type !== 'trailer' && !usedVehicleIds.has(v.id));
  const freeTrailers = vehicles.filter((v) => v.type === 'trailer' && !usedVehicleIds.has(v.id));
  const editUnitTrailer = unitForm ? vehicles.find((v) => v.id === unitForm.trailer_id) : null;

  return (
    <div>
      <AppHeader subtitle="Detail cesty" />
      <div className="container-wide" style={{ paddingTop: 24 }}>
        <div className="card mb-16">
          <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2>{trip.event_name ? `Výjezd na ${trip.event_name}` : 'Výjezd'} {units.length > 1 && <span className="badge badge-in">konvoj {units.length}×</span>}</h2>
              <p className="text-muted">{[trip.origin, trip.destination].filter(Boolean).join(' → ') || 'Trasa nezadána'}</p>
              <p style={{ marginTop: 6 }}>
                <span className={`badge ${TRIP_STATUS_BADGE[trip.status]}`}>{TRIP_STATUS[trip.status]}</span>
                {trip.unit_count > 0 && <span className="text-muted" style={{ marginLeft: 8, fontSize: '0.85rem' }}>{trip.arrived_count}/{trip.unit_count} vozidel na místě</span>}
              </p>
            </div>
            <div className="flex gap-8" style={{ flexDirection: 'column', alignItems: 'flex-end' }}>
              {['chapter_rep', 'superadmin'].includes(member.role) && trip.arrived_count > 0 && (
                <button className="btn btn-outline btn-sm" onClick={() => navigate(`/cesty/${id}/vyuctovani`)}>🧾 Vyúčtování</button>
              )}
              {canManage && <button className="btn btn-danger btn-sm" onClick={removeTrip}>Smazat cestu</button>}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, marginTop: 12, fontSize: '0.85rem' }}>
            <div><span className="text-muted">Spolek:</span> {trip.chapter_name}</div>
            <div><span className="text-muted">Předání:</span> {HANDOVER_MODES[trip.handover_mode]}</div>
            <div><span className="text-muted">Výjezd:</span> {trip.departed_at ? new Date(trip.departed_at).toLocaleString('cs-CZ') : '—'}</div>
            <div><span className="text-muted">Příjezd:</span> {trip.arrived_at ? new Date(trip.arrived_at).toLocaleString('cs-CZ') : '—'}</div>
          </div>
          {trip.note && <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: 8 }}>📝 {trip.note}</p>}
        </div>

        {canManage && NEXT_STATUSES[trip.status]?.length > 0 && (
          <div className="card mb-16">
            <div className="flex gap-8 items-center" style={{ flexWrap: 'wrap' }}>
              <strong>Změnit stav:</strong>
              {NEXT_STATUSES[trip.status].map((s) => (
                <button key={s} className="btn btn-sm btn-outline" onClick={() => changeStatus(s)}>→ {TRIP_STATUS[s]}</button>
              ))}
            </div>
          </div>
        )}

        {error && <div className="alert alert-warning mb-16">{error}</div>}

        {/* ── Konvoj: jednotky (vozidla + vleky) ────────────────────────────── */}
        <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3>Vozidla konvoje ({units.length})</h3>
          <div className="flex gap-8">
            {cargo.length > 0 && <a className="btn btn-outline btn-sm" href={tripsApi.cargoCsvUrl(id)} download>⬇ CSV nákladu</a>}
            {canManage && !unitForm && <button className="btn btn-primary btn-sm" onClick={() => { setUnitForm({ ...emptyUnit }); setError(''); }}>+ Přidat vozidlo</button>}
          </div>
        </div>

        {unitForm && canManage && (
          <div className="card mb-16">
            <h4 className="mb-12">Přidat vozidlo do konvoje</h4>
            <form onSubmit={addUnit}>
              <div className="flex gap-8">
                <div className="form-group flex-1">
                  <label>Vozidlo *</label>
                  <select className="form-input" value={unitForm.vehicle_id} onChange={(e) => setUnitForm({ ...unitForm, vehicle_id: e.target.value })} required>
                    <option value="">— vyberte —</option>
                    {freeVehicles.map((v) => <option key={v.id} value={v.id}>{v.name}{v.plate ? ` (${v.plate})` : ''}</option>)}
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label>Řidič</label>
                  <select className="form-input" value={unitForm.driver_id} onChange={(e) => setUnitForm({ ...unitForm, driver_id: e.target.value })}>
                    <option value="">— nezadáno —</option>
                    {drivers.map((d) => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Vlek <span className="text-muted">(volitelné)</span></label>
                <select className="form-input" value={unitForm.trailer_id} onChange={(e) => setUnitForm({ ...unitForm, trailer_id: e.target.value })}>
                  <option value="">— bez vleku —</option>
                  {freeTrailers.map((t) => <option key={t.id} value={t.id}>{t.name}{t.plate ? ` (${t.plate})` : ''}</option>)}
                  {editUnitTrailer && !freeTrailers.some((t) => t.id === editUnitTrailer.id) && <option value={editUnitTrailer.id}>{editUnitTrailer.name}</option>}
                </select>
              </div>
              <div className="flex gap-8">
                <button type="submit" className="btn btn-primary flex-1">Přidat do konvoje</button>
                <button type="button" className="btn btn-outline" onClick={() => { setUnitForm(null); setError(''); }}>Zrušit</button>
              </div>
              {freeVehicles.length === 0 && <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: 8 }}>Žádná volná vozidla – všechna jsou už v konvoji nebo na jiné cestě.</p>}
            </form>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {units.map((u) => {
            const items = cargoForUnit(u.id);
            return (
              <div key={u.id} className="card">
                <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>
                      🚚 {u.vehicle_name}{u.vehicle_plate ? ` · ${u.vehicle_plate}` : ''}
                      {u.trailer_name && <span className="text-muted" style={{ fontWeight: 400 }}> + 🛒 {u.trailer_name}{u.trailer_plate ? ` (${u.trailer_plate})` : ''}</span>}
                    </div>
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Řidič: {u.driver_first_name ? `${u.driver_first_name} ${u.driver_last_name}` : '—'}
                      {u.arrived_at && <span className="badge badge-active" style={{ marginLeft: 8 }}>na místě ✓</span>}
                    </div>
                  </div>
                  {canManage && !u.arrived_at && <button className="btn btn-danger btn-sm" onClick={() => removeUnit(u.id)}>Odebrat</button>}
                </div>

                {/* náklad jednotky */}
                <div style={{ marginTop: 10 }}>
                  {items.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: '0.85rem' }}>Zatím nenaloženo.</p>
                  ) : (
                    <table style={{ fontSize: '0.9rem' }}>
                      <tbody>
                        {items.map((c) => {
                          const isBox = !!c.container_id;
                          const expanded = boxContents[c.id];
                          return (
                            <Fragment key={c.id}>
                              <tr>
                                <td style={{ width: 28 }}>{isBox ? '📦' : (c.placement === 'trailer' ? '🛒' : '🚚')}</td>
                                <td>
                                  {isBox ? (
                                    <span style={{ cursor: 'pointer' }} onClick={() => toggleBoxContents(c)}>
                                      {expanded ? '▾' : '▸'} {c.container_name || c.item_name}
                                      {c.container_evidence ? <span className="text-muted"> · {c.container_evidence}</span> : null}
                                    </span>
                                  ) : c.item_name}
                                  {c.delivered_at && <span className="badge badge-out" style={{ marginLeft: 6 }}>doručeno</span>}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>{isBox ? `${c.container_item_count} pol.` : `${c.quantity} ${c.unit}`}</td>
                                <td>{isBox ? <span className="badge badge-in">box</span> : (c.source_inventory_id ? <span className="badge badge-active">sklad</span> : <span className="badge badge-none">vlastní</span>)}</td>
                                <td style={{ width: 36 }}>{!c.delivered_at && <button className="btn btn-danger btn-sm" onClick={() => removeCargo(c.id)}>×</button>}</td>
                              </tr>
                              {isBox && expanded && expanded.map((it) => (
                                <tr key={c.id + it.id}>
                                  <td></td>
                                  <td className="text-muted" style={{ fontSize: '0.82rem', paddingLeft: 18 }}>• {it.item_name}</td>
                                  <td className="text-muted" style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{it.quantity} {it.unit}</td>
                                  <td></td><td></td>
                                </tr>
                              ))}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {trip.status !== 'completed' && trip.status !== 'cancelled' && !u.arrived_at && (
                    <button className="btn btn-outline btn-sm mt-8" onClick={() => openCargoForm(u)}>📦 Naložit materiál</button>
                  )}
                </div>

                {/* nakládací panel pro tuto jednotku */}
                {cargoForm && cargoForm.unitId === u.id && (
                  <div style={{ marginTop: 12, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
                    <div className="flex gap-8" style={{ marginBottom: 10 }}>
                      <button type="button" className={`btn btn-sm flex-1 ${cargoForm.placement === 'vehicle' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setCargoForm({ ...cargoForm, placement: 'vehicle' })}>🚚 Do vozidla</button>
                      <button type="button" className={`btn btn-sm flex-1 ${cargoForm.placement === 'trailer' ? 'btn-primary' : 'btn-outline'}`}
                        disabled={!u.trailer_id}
                        onClick={() => setCargoForm({ ...cargoForm, placement: 'trailer' })}>🛒 Do vleku</button>
                    </div>
                    <div className="flex gap-8" style={{ marginBottom: 10 }}>
                      <button type="button" className={`btn btn-sm flex-1 ${cargoForm.mode === 'inventory' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setCargoForm({ ...cargoForm, mode: 'inventory' })}>Sklad</button>
                      <button type="button" className={`btn btn-sm flex-1 ${cargoForm.mode === 'box' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setCargoForm({ ...cargoForm, mode: 'box' })}>📦 Boxy</button>
                      <button type="button" className={`btn btn-sm flex-1 ${cargoForm.mode === 'custom' ? 'btn-primary' : 'btn-outline'}`}
                        onClick={() => setCargoForm({ ...cargoForm, mode: 'custom' })}>Vlastní</button>
                    </div>

                    {cargoForm.mode === 'box' ? (
                      (() => {
                        const avail = boxes.filter((bx) => bx.on_trip === 0 && !cargo.some((c) => c.container_id === bx.id));
                        return avail.length === 0 ? (
                          <p className="text-muted" style={{ fontSize: '0.85rem' }}>Žádné volné boxy (všechny jsou prázdné, na cestě, nebo už naložené).</p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {avail.map((bx) => (
                              <div key={bx.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 8 }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>📦 {bx.name}{bx.evidence_number ? ` · ${bx.evidence_number}` : ''}</div>
                                  <div className="text-muted" style={{ fontSize: '0.78rem' }}>{bx.item_count} položek</div>
                                </div>
                                <button type="button" className="btn btn-primary btn-sm" onClick={() => loadBox(u, cargoForm.placement, bx)}>Naložit</button>
                              </div>
                            ))}
                          </div>
                        );
                      })()
                    ) : cargoForm.mode === 'inventory' ? (
                      inventory.length === 0 ? (
                        <p className="text-muted" style={{ fontSize: '0.85rem' }}>Sklad spolku je prázdný.</p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {inventory.map((it) => {
                            const loaded = loadedCargo(u.id, cargoForm.placement, it.id);
                            const qty = loaded ? loaded.quantity : 0;
                            const isBusy = busy.has(it.id);
                            return (
                              <div key={it.id} style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                                border: '1px solid var(--color-border)', borderRadius: 8,
                                background: qty > 0 ? 'var(--color-primary-bg, #f0f7ff)' : 'transparent',
                              }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{it.catalog_name || it.custom_name || 'Položka'}</div>
                                  <div className="text-muted" style={{ fontSize: '0.78rem' }}>skladem {it.quantity} {it.unit}</div>
                                </div>
                                <div className="flex" style={{ alignItems: 'center', gap: 8 }}>
                                  <button type="button" className="btn btn-outline" style={{ width: 40, height: 40, padding: 0, fontSize: '1.3rem', lineHeight: 1 }}
                                    disabled={isBusy || qty === 0} onClick={() => decItem(u, cargoForm.placement, it)}>−</button>
                                  <span style={{ minWidth: 32, textAlign: 'center', fontWeight: 700, fontSize: '1.1rem' }}>{qty}</span>
                                  <button type="button" className="btn btn-primary" style={{ width: 40, height: 40, padding: 0, fontSize: '1.3rem', lineHeight: 1 }}
                                    disabled={isBusy || it.quantity <= 0} onClick={() => incItem(u, cargoForm.placement, it)}>+</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )
                    ) : (
                      <form onSubmit={addCargo}>
                        <div className="flex gap-8">
                          <div className="form-group flex-1">
                            <label>Název položky</label>
                            <input className="form-input" value={cargoForm.custom_name} onChange={(e) => setCargoForm({ ...cargoForm, custom_name: e.target.value })} />
                          </div>
                          <div className="form-group" style={{ width: 100 }}>
                            <label>Jednotka</label>
                            <input className="form-input" value={cargoForm.unit} onChange={(e) => setCargoForm({ ...cargoForm, unit: e.target.value })} />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Množství</label>
                          <input type="number" min="0" step="any" className="form-input" style={{ fontSize: '1.1rem' }} value={cargoForm.quantity} onChange={(e) => setCargoForm({ ...cargoForm, quantity: e.target.value })} />
                        </div>
                        <button type="submit" className="btn btn-primary btn-block">Naložit vlastní položku</button>
                      </form>
                    )}

                    <button type="button" className="btn btn-outline btn-sm mt-8" onClick={() => { setCargoForm(null); setError(''); }}>Hotovo</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Předávací události ──────────────────────────────────────────────── */}
      {handoverEvents.length > 0 && (
        <div className="container-wide">
          <div className="card mt-16">
            <h3 className="mb-12">Předání na MU</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {handoverEvents.map((ev) => (
                <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
                  <span style={{ fontSize: 20 }}>{ev.method === 'qr' ? '📱' : '✋'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{ev.vehicle_name || ev.checkpoint_name} {ev.scope === 'convoy' && <span className="badge badge-in">konvoj</span>}</div>
                    <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                      {ev.checkpoint_name}{ev.confirmed_first_name ? ` · potvrdil: ${ev.confirmed_first_name} ${ev.confirmed_last_name}` : ''} · {METHOD_LABEL[ev.method] || ev.method}
                    </div>
                    {ev.note && <div style={{ fontSize: '0.82rem', fontStyle: 'italic' }}>{ev.note}</div>}
                  </div>
                  <span className="text-muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{new Date(ev.created_at).toLocaleString('cs-CZ')}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
